import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  alpha,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import SearchableClientDropdown from '@/components/shared/SearchableClientDropdown';
import { Tag } from '@/components/ui/tag';
import {
  DynamicQuestionnaireFlow,
  getOrderedQuestions,
  injectedAnswerKey,
} from '@/components/questionnaires/json-driven/DynamicQuestionnaireFlow';
import { getVisibleOrderedQuestions } from '@/components/questionnaires/json-driven/visibleQuestions';
import {
  buildQuestionnairePrefillScopeKey,
  buildQuestionnairePrefillFromAssignedClient,
  mergeSharedQuestionnaireAssignedClient,
  resolveQuestionnaireDemographicContext,
  triggerContextFromAssignedClient,
} from '@/components/questionnaires/json-driven/clientContext';
import { getCurrentUser } from '@/lib/api/auth';
import { getQuestionKey } from '@/components/questionnaires/json-driven/questionKey';
import { isEffectivelyRequired } from '@/components/questionnaires/json-driven/effectiveRequired';
import type { JsonAnswerValue } from '@/components/questionnaires/json-driven/QuestionRenderer';
import {
  isAnsweredValue,
  normalizeQuestionAnswerForSubmit,
  OTHER_TEXT_SUFFIX,
} from '@/components/questionnaires/json-driven/QuestionRenderer';
import { QuestionnaireAdditionalQuestionsPanel } from '@/components/questionnaires/review/QuestionnaireAdditionalQuestionsPanel';
import type { JsonQuestion, JsonQuestionOption, JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import type { TriggerConditionContext, TriggeredBlock } from '@/lib/questionnaire-triggers';
import type { Client } from '@/types/client';
import type { AdditionalQuestionResponseType } from '@/components/questionnaires/review/additionalQuestionConfig';
import {
  getQuestionnaireByIdForTriggers,
  getSharedQuestionnaire,
  INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE,
  saveQuestionnaireDraft,
  submitQuestionnaire,
  type SharedQuestionnaireAssignedClient,
  type SharedQuestionnaireResponse,
  type SaveQuestionnaireDraftResponse,
  type SubmitQuestionnaireBlock,
  type SubmitQuestionnairePayload,
} from '@/lib/api/questionnaires';
import {
  applyPendingSavedResponses,
  buildQuestionnaireBlocks,
  countNonEmptyResponsesInBlocks,
  formatDraftSavedAt,
  getTriggeredSectionAnswers,
  isQuestionnaireInstanceLocked,
  savedResponseFingerprint,
  savedResponsesFromBlocks,
} from '@/lib/questionnaire-draft';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import InlineFeedback, { type InlineFeedbackProps } from '@/components/shared/InlineFeedback';
import clockCircleIcon from '@/assets/images/clock_prv.svg';
import notebookIcon from '@/assets/images/Notebook_prv.svg';
import usersGroupRoundedIcon from '@/assets/images/assigned_prv.svg';
import verifiedCheckIcon from '@/assets/images/VerifiedCheck_prv.svg';
import trashIcon from '@/assets/images/trash-bin-trash.svg';

type DialogFeedback = Pick<InlineFeedbackProps, 'variant' | 'title' | 'message'>;
import layer1Graphic from '@/assets/images/layer-1.svg';
import downloadPreviewIcon from '@/assets/images/download_prv.svg';
import { formatDelimitedDisplayLabel, formatDisplayLabel } from '@/utils/display-labels';
import {
  ADDITIONAL_QUESTIONS_SECTION_ID,
  ACTIVE_SECTION_COLOR,
  ACTIVE_SECTION_ICON_BG,
  ACTIVE_SECTION_ICON_COLOR,
  ACTIVE_SECTION_ROW_BG,
  type QuestionnaireSectionGroup,
  type QuestionnaireSourceType,
  type QuestionTypeChipData,
  type ShareActionIntent,
} from './types';
import { getQuestionMetaChips, getQuestionTypeLabel } from './utils';

const pageTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '28px',
  lineHeight: '42px',
  letterSpacing: '0.38px',
  color: '#f8fbff',
} as const;

function SummaryMetric({
  icon,
  value,
  label,
}: {
  icon: string;
  value: string | number;
  label: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="img" src={icon} alt="" sx={{ width: 16, height: 16, display: 'block' }} />
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '13px',
          lineHeight: '18px',
          letterSpacing: 0,
          color: '#fff',
        }}
      >
        {value} {label}
      </Typography>
    </Box>
  );
}

export function QuestionnaireFinalDetailLoadingSection() {
  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fff',
          p: { xs: 3, md: 4 },
        }}
      >
        <Skeleton variant="rounded" height={28} width={320} sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" height={142} sx={{ mt: 3, borderRadius: '24px' }} />
        <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '240px 1fr' }, gap: 3 }}>
          <Skeleton variant="rounded" height={420} sx={{ borderRadius: '24px' }} />
          <Skeleton variant="rounded" height={720} sx={{ borderRadius: '24px' }} />
        </Box>
      </Paper>
    </Box>
  );
}

interface QuestionnaireFinalDetailErrorSectionProps {
  message: string;
  onBack: () => void;
}

export function QuestionnaireFinalDetailErrorSection({
  message,
  onBack,
}: QuestionnaireFinalDetailErrorSectionProps) {
  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fff',
          p: { xs: 3, md: 4 },
        }}
      >
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '26px', color: '#0f172a' }}>
          Questionnaire not available
        </Typography>
        <Typography sx={{ mt: 1.5, fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#667085' }}>
          {message}
        </Typography>
        <Button
          variant="contained"
          onClick={onBack}
          sx={{
            mt: 3,
            borderRadius: '14px',
            bgcolor: '#25a8da',
            fontFamily: 'Inter, sans-serif',
            '&:hover': { bgcolor: '#1498ca' },
          }}
        >
          Back to Questionnaires
        </Button>
      </Paper>
    </Box>
  );
}

interface QuestionnaireFinalDetailHeaderSectionProps {
  section: JsonQuestionnaireSection;
  questionnairesBackPath: string;
  questionnaireTypeLabel: string;
  description: string;
  questionCount: number;
  durationLabel: string;
  assignedClientsCount: number;
  responsesCount: number;
  type: QuestionnaireSourceType;
  isDraftQuestionnaire: boolean;
  publishPending: boolean;
  downloadPending: boolean;
  onEditName: () => void;
  onAssignClients: () => void;
  onPublish: () => void;
  onOpenShareDialog: (intent: ShareActionIntent) => void;
  onDownloadBlankForm: () => void;
}

export function QuestionnaireFinalDetailHeaderSection({
  section,
  questionnairesBackPath,
  questionnaireTypeLabel,
  description,
  questionCount,
  durationLabel,
  assignedClientsCount,
  responsesCount,
  type,
  isDraftQuestionnaire,
  publishPending,
  downloadPending,
  onEditName,
  onAssignClients,
  onPublish,
  onOpenShareDialog,
  onDownloadBlankForm,
}: QuestionnaireFinalDetailHeaderSectionProps) {
  const shouldShowDescription = type !== 'custom' && description.trim().length > 0;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fcfeff',
          px: 2,
          py: 1.25,
        }}
      >
        <Breadcrumbs separator="/" aria-label="breadcrumb">
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={questionnairesBackPath}
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
          >
            Questionnaires
          </Link>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
            {section.title}
          </Typography>
        </Breadcrumbs>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          maxWidth: { lg: 1280 },
          minHeight: 242,
          height: { lg: 242 },
          alignSelf: 'flex-start',
          borderRadius: '10px',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 2.75 },
          background: 'linear-gradient(135deg, #2ba9da 0%, #148fbd 62%, #0f86af 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'stretch',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: { xs: 0.75, md: 1 },
          }}
        >
          <Box
            component="img"
            src={layer1Graphic}
            alt=""
            sx={{
              width: { xs: 220, sm: 260, md: 297 },
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'right center',
              display: 'block',
            }}
          />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'flex-start' },
              gap: 2,
            }}
          >
            <Box sx={{ maxWidth: 820, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '1.44px',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.84)',
                }}
              >
                {questionnaireTypeLabel}
              </Typography>
              <Typography sx={{ ...pageTitleSx, mt: '22px' }}>{section.title}</Typography>
              {shouldShowDescription ? (
                <Typography
                  sx={{
                    mt: '12px',
                    maxWidth: 760,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '13px',
                    lineHeight: '140%',
                    letterSpacing: '-0.15px',
                    color: 'rgba(255,255,255,0.86)',
                  }}
                >
                  {description}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                flexShrink: { xs: 1, lg: 0 },
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                gap: 1,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              {type === 'custom' ? (
                <Button
                  variant="outlined"
                  onClick={onEditName}
                  sx={{
                    width: 170,
                    minWidth: 170,
                    height: 40,
                    minHeight: 40,
                    borderRadius: '10px',
                    borderWidth: '1px',
                    borderColor: alpha('#ffffff', 0.52),
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '13px',
                    lineHeight: '18px',
                    '&:hover': {
                      borderWidth: '1px',
                      borderColor: alpha('#ffffff', 0.72),
                      bgcolor: alpha('#ffffff', 0.08),
                    },
                  }}
                >
                  Edit Title
                </Button>
              ) : null}
              {!isDraftQuestionnaire ? (
                <Button
                  variant="contained"
                  onClick={onAssignClients}
                  sx={{
                    width: 170,
                    minWidth: 170,
                    height: 40,
                    minHeight: 40,
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    color: '#0f172a',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '13px',
                    lineHeight: '18px',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#f3f8fb', boxShadow: 'none' },
                  }}
                >
                  Assign to Clients
                </Button>
              ) : null}
              {type === 'custom' && isDraftQuestionnaire ? (
                <Button
                  variant="outlined"
                  onClick={onPublish}
                  disabled={publishPending}
                  sx={{
                    width: 170,
                    minWidth: 170,
                    height: 40,
                    minHeight: 40,
                    borderRadius: '10px',
                    borderWidth: '1px',
                    borderColor: alpha('#ffffff', 0.52),
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '13px',
                    lineHeight: '18px',
                    '&:hover': {
                      borderWidth: '1px',
                      borderColor: alpha('#ffffff', 0.72),
                      bgcolor: alpha('#ffffff', 0.08),
                    },
                    '&.Mui-disabled': {
                      borderColor: alpha('#ffffff', 0.28),
                      color: alpha('#ffffff', 0.45),
                    },
                  }}
                >
                  {publishPending ? 'Publishing...' : 'Publish'}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => onOpenShareDialog('fill-manually')}
                  sx={{
                    width: 170,
                    minWidth: 170,
                    height: 40,
                    minHeight: 40,
                    borderRadius: '10px',
                    borderWidth: '1px',
                    borderColor: alpha('#ffffff', 0.52),
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '13px',
                    lineHeight: '18px',
                    '&:hover': {
                      borderWidth: '1px',
                      borderColor: alpha('#ffffff', 0.72),
                      bgcolor: alpha('#ffffff', 0.08),
                    },
                  }}
                >
                  Fill Manually
                </Button>
              )}
              {!isDraftQuestionnaire ? (
                <IconButton
                  onClick={() => onOpenShareDialog('copy-link')}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '12px',
                    bgcolor: '#0b1116',
                    color: '#fff',
                    '&:hover': { bgcolor: '#000' },
                  }}
                >
                  <LinkRoundedIcon fontSize="small" />
                </IconButton>
              ) : null}
              {!isDraftQuestionnaire ? (
                <IconButton
                  onClick={onDownloadBlankForm}
                  disabled={downloadPending}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '12px',
                    bgcolor: '#0b1116',
                    color: '#fff',
                    '&:hover': { bgcolor: '#000' },
                    '&.Mui-disabled': {
                      bgcolor: '#0b1116',
                      color: 'rgba(255,255,255,0.6)',
                    },
                  }}
                >
                  {downloadPending ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : (
                    <Box
                      component="img"
                      src={downloadPreviewIcon}
                      alt=""
                      sx={{ width: 18, height: 18, display: 'block' }}
                    />
                  )}
                </IconButton>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ height: '24px', flexShrink: 0 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.25 }}>
            <SummaryMetric icon={notebookIcon} value={questionCount} label="questions" />
            {type !== 'custom' ? <SummaryMetric icon={clockCircleIcon} value={durationLabel} label="" /> : null}
            <SummaryMetric icon={usersGroupRoundedIcon} value={assignedClientsCount} label="assigned" />
            <SummaryMetric icon={verifiedCheckIcon} value={responsesCount} label="responses" />
          </Box>
        </Box>
      </Paper>
    </>
  );
}

interface QuestionnaireFinalDetailSidebarSectionProps {
  groupedSections: QuestionnaireSectionGroup[];
  expandedSectionIds: string[];
  onSelectSection: (id: string) => void;
  additionalQuestionsCount: number;
  isAdditionalQuestionsActive: boolean;
  onSelectAdditionalQuestions: () => void;
  questionTypeChips: QuestionTypeChipData[];
  showAdditionalQuestionsSection?: boolean;
}

export function QuestionnaireFinalDetailSidebarSection({
  groupedSections,
  expandedSectionIds,
  onSelectSection,
  additionalQuestionsCount,
  isAdditionalQuestionsActive,
  onSelectAdditionalQuestions,
  questionTypeChips,
  showAdditionalQuestionsSection = true,
}: QuestionnaireFinalDetailSidebarSectionProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: { lg: 'sticky' },
        top: { lg: 88 },
        width: { xs: '100%', lg: 260 },
        height: { lg: 840 },
        maxHeight: { lg: 840 },
        alignSelf: 'start',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        overflow: 'auto',
        borderRadius: '14px',
        border: '1px solid #dbe6eb',
        bgcolor: '#fff',
        pt: '20px',
        pr: '1px',
        pb: '20px',
        pl: '1px',
      }}
    >
      <Typography
        sx={{
          px: '19px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '12px',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#98a2b3',
        }}
      >
        Sections
      </Typography>

      <Box sx={{ display: 'grid', gap: '14px', px: '13px', flex: '1 1 auto', alignContent: 'start' }}>
        {groupedSections.map((group) => {
          const Icon = group.icon;
          const isActive = expandedSectionIds.includes(group.id);

          return (
            <Box
              key={group.id}
              onClick={() => onSelectSection(group.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                borderRadius: '12px',
                px: 1.1,
                py: 1,
                cursor: 'pointer',
                bgcolor: isActive ? ACTIVE_SECTION_ROW_BG : 'transparent',
                '&:hover': { bgcolor: isActive ? alpha(ACTIVE_SECTION_COLOR, 0.16) : '#f8fafc' },
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isActive ? ACTIVE_SECTION_ICON_BG : group.iconBg,
                  color: isActive ? ACTIVE_SECTION_ICON_COLOR : group.iconColor,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 14 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0.41px',
                    color: isActive ? ACTIVE_SECTION_COLOR : '#344054',
                    textTransform: 'uppercase',
                  }}
                >
                  {group.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: 0,
                    color: '#98a2b3',
                  }}
                >
                  {group.questions.length} QUESTIONS
                </Typography>
              </Box>
            </Box>
          );
        })}

        {showAdditionalQuestionsSection && (
          <Box
            onClick={onSelectAdditionalQuestions}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              borderRadius: '12px',
              px: 1.1,
              py: 1,
              cursor: 'pointer',
              bgcolor: isAdditionalQuestionsActive ? ACTIVE_SECTION_ROW_BG : 'transparent',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#e7efff',
                color: '#4f8cff',
                flexShrink: 0,
              }}
            >
              <TipsAndUpdatesRoundedIcon sx={{ fontSize: 14 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '0.41px',
                  color: isAdditionalQuestionsActive ? ACTIVE_SECTION_COLOR : '#344054',
                  textTransform: 'uppercase',
                }}
              >
                Additional Questions
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: 0,
                  color: '#98a2b3',
                }}
              >
                {additionalQuestionsCount} QUESTIONS
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ borderTop: '1px solid #eef2f6', mt: 2, pt: 2 }} />
      <Typography
        sx={{
          px: '19px',
          fontFamily: 'Arial, sans-serif',
          fontSize: '12px',
          color: '#98a2b3',
        }}
      >
        Question types
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: '19px' }}>
        {questionTypeChips.map((chip) => (
          <Tag
            key={chip.type}
            variant="neutral"
            size="xs"
            className="h-[22px] min-w-[62.68px] rounded-[10px] font-[Inter,sans-serif] text-xs font-semibold leading-4"
            sx={{
              borderBottom: `1px solid ${chip.style.borderColor}`,
              bgcolor: chip.style.bgcolor,
              color: chip.style.color,
            }}
          >
            {chip.label}
          </Tag>
        ))}
      </Box>
    </Paper>
  );
}

interface QuestionnaireFinalDetailQuestionSectionsProps {
  groupedSections: QuestionnaireSectionGroup[];
  expandedSectionIds: string[];
  onToggleSectionExpanded: (id: string) => void;
  canDeleteQuestions: boolean;
  deletePendingQuestionId?: string;
  onRequestDeleteQuestion: (questionId: string) => void;
  additionalQuestions: JsonQuestion[];
  additionalQuestionsCount: number;
  additionalQuestionText: string;
  additionalQuestionRequired: boolean;
  additionalQuestionResponseType: AdditionalQuestionResponseType;
  additionalQuestionOptionLines: string;
  onAdditionalQuestionTextChange: (value: string) => void;
  onAdditionalQuestionRequiredChange: (required: boolean) => void;
  onAdditionalQuestionResponseTypeChange: (type: AdditionalQuestionResponseType) => void;
  onAdditionalQuestionOptionLinesChange: (value: string) => void;
  onSaveAdditionalQuestion: () => void;
  onOpenCopyDialog: () => void;
  isSavingAdditionalQuestion: boolean;
  maxAdditionalQuestionsReached: boolean;
  isSaveAdditionalQuestionDisabled: boolean;
  showAdditionalQuestionsPanel?: boolean;
}

export function QuestionnaireFinalDetailQuestionSections({
  groupedSections,
  expandedSectionIds,
  onToggleSectionExpanded,
  canDeleteQuestions,
  deletePendingQuestionId,
  onRequestDeleteQuestion,
  additionalQuestions,
  additionalQuestionsCount,
  additionalQuestionText,
  additionalQuestionRequired,
  additionalQuestionResponseType,
  additionalQuestionOptionLines,
  onAdditionalQuestionTextChange,
  onAdditionalQuestionRequiredChange,
  onAdditionalQuestionResponseTypeChange,
  onAdditionalQuestionOptionLinesChange,
  onSaveAdditionalQuestion,
  onOpenCopyDialog,
  isSavingAdditionalQuestion,
  maxAdditionalQuestionsReached,
  isSaveAdditionalQuestionDisabled,
  showAdditionalQuestionsPanel = true,
}: QuestionnaireFinalDetailQuestionSectionsProps) {
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {groupedSections.map((group) => {
        const Icon = group.icon;
        const expanded = expandedSectionIds.includes(group.id);

        return (
          <Paper
            key={group.id}
            id={group.id}
            elevation={0}
            sx={{
              overflow: 'hidden',
              borderRadius: '18px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fff',
            }}
          >
            <Box
              onClick={() => onToggleSectionExpanded(group.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 2.25,
                py: 1.8,
                cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: expanded ? ACTIVE_SECTION_ICON_BG : group.iconBg,
                    color: expanded ? ACTIVE_SECTION_ICON_COLOR : group.iconColor,
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 15 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0.41px',
                    color: '#0A0A0A',
                    textTransform: 'uppercase',
                  }}
                >
                  {group.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: 0,
                    color: '#98a2b3',
                  }}
                >
                  • {group.questions.length} QUESTIONS
                </Typography>
              </Box>

              <ExpandMoreRoundedIcon
                sx={{
                  fontSize: 18,
                  color: expanded ? ACTIVE_SECTION_ICON_COLOR : '#98a2b3',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>

            {expanded && (
              <Box sx={{ borderTop: '1px solid #eef2f6' }}>
                {group.questions.map((question, index) => {
                  const chips = getQuestionMetaChips(question);
                  const deleteDisabled = deletePendingQuestionId === question._id;

                  return (
                    <Box
                      key={question._id}
                      sx={{
                        px: 2.25,
                        py: 1.8,
                        borderBottom:
                          index === group.questions.length - 1 ? 'none' : '1px solid #eef2f6',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '84px minmax(0, 1fr)' },
                          gap: 1.5,
                          alignItems: { xs: 'start', md: 'center' },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: 0,
                            color: '#98a2b3',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {`${group.shortCode}-${String(index + 1).padStart(2, '0')}`}
                        </Typography>

                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1.25,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'flex-start', md: 'center' },
                                gap: 0.75,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  letterSpacing: '-0.15px',
                                  color: '#0f172a',
                                }}
                              >
                                {question.questionText}
                                {question.validation?.required ? ' *' : ''}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Arial, sans-serif',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                  color: '#98a2b3',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                • {getQuestionTypeLabel(question.type)}
                              </Typography>
                            </Box>

                            {canDeleteQuestions && (
                              <IconButton
                                size="small"
                                disabled={deleteDisabled}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onRequestDeleteQuestion(question._id);
                                }}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  color: '#f97066',
                                  flexShrink: 0,
                                  '&:hover': {
                                    bgcolor: 'rgba(249, 112, 102, 0.08)',
                                  },
                                }}
                              >
                                <Box component="img" src={trashIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                              </IconButton>
                            )}
                          </Box>

                          {chips.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {chips.map((chip) => (
                                <Tag
                                  key={`${question._id}-${chip}`}
                                  variant="neutral"
                                  size="xs"
                                  className="h-[22px] min-w-[62.68px] rounded-[10px] border-b border-b-[#E8E2D5] bg-[#F6EDDC] font-[Inter,sans-serif] text-xs font-semibold leading-4 text-[#7C6F52]"
                                >
                                  {chip}
                                </Tag>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        );
      })}

      {showAdditionalQuestionsPanel && (
        <Box id={ADDITIONAL_QUESTIONS_SECTION_ID}>
          <Box>
            <QuestionnaireAdditionalQuestionsPanel
              questionCount={additionalQuestionsCount}
              existingQuestions={additionalQuestions.map((question, index) => ({
                id: question._id,
                code: `AQ-${String(index + 1).padStart(2, '0')}`,
                questionText: question.questionText,
                typeLabel: getQuestionTypeLabel(question.type),
                chips: getQuestionMetaChips(question),
                required: Boolean(question.validation?.required),
              }))}
              questionText={additionalQuestionText}
              required={additionalQuestionRequired}
              responseType={additionalQuestionResponseType}
              optionLines={additionalQuestionOptionLines}
              onDeleteExistingQuestion={onRequestDeleteQuestion}
              deletingQuestionId={deletePendingQuestionId}
              canDeleteQuestions={canDeleteQuestions}
              onQuestionTextChange={onAdditionalQuestionTextChange}
              onRequiredChange={onAdditionalQuestionRequiredChange}
              onResponseTypeChange={onAdditionalQuestionResponseTypeChange}
              onOptionLinesChange={onAdditionalQuestionOptionLinesChange}
              onSave={onSaveAdditionalQuestion}
              onOpenCopyFromQuestionnaire={onOpenCopyDialog}
              isSaving={isSavingAdditionalQuestion}
              disableAddQuestionBox={maxAdditionalQuestionsReached}
              saveDisabled={isSaveAdditionalQuestionDisabled}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

interface QuestionnaireDeleteQuestionDialogProps {
  open: boolean;
  isSubmitting: boolean;
  feedback?: DialogFeedback | null;
  onClose: () => void;
  onConfirm: () => void;
}

export function QuestionnaireDeleteQuestionDialog({
  open,
  isSubmitting,
  feedback,
  onClose,
  onConfirm,
}: QuestionnaireDeleteQuestionDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          px: { xs: 1.75, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
        },
      }}
    >
      <Box sx={{ display: 'grid', justifyItems: 'center', textAlign: 'center' }}>
        <Box
          sx={{
            width: { xs: 66, sm: 72 },
            height: { xs: 66, sm: 72 },
            borderRadius: '14px',
            bgcolor: '#fff1f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="img" src={trashIcon} alt="" sx={{ width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 }, display: 'block' }} />
        </Box>

        <Typography
          sx={{
            mt: 1.75,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: { xs: '24px', sm: '28px' },
            lineHeight: { xs: '32px', sm: '36px' },
            color: '#0b1116',
            letterSpacing: '-0.03em',
          }}
        >
          Remove question?
        </Typography>

        <Typography
          sx={{
            mt: 0.75,
            maxWidth: 420,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: { xs: '13px', sm: '14px' },
            lineHeight: { xs: '20px', sm: '22px' },
            color: '#667085',
            letterSpacing: '-0.02em',
          }}
        >
          Are you sure you want to remove this question from the questionnaire?
        </Typography>

        {feedback ? (
          <Box sx={{ mt: 1.5, width: '100%' }}>
            <InlineFeedback
              variant={feedback.variant}
              title={feedback.title}
              message={feedback.message}
            />
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 2.25,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              bgcolor: '#25a8da',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: 1.2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="outlined"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              borderColor: '#d0d5dd',
              color: '#344054',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: 1.2,
              textTransform: 'none',
              '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={18} thickness={6} sx={{ color: '#344054' }} />
                Removing...
              </Box>
            ) : (
              'Yes, remove'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

interface QuestionnaireFinalDetailShareDialogProps {
  open: boolean;
  shareActionIntent: ShareActionIntent;
  isSubmitting: boolean;
  selectedClientId: string;
  searchText: string;
  clients: Client[];
  isClientsLoading: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  onSearchTextChange: (value: string) => void;
  onClientChange: (clientId: string, client: Client | null) => void;
}

interface FillManualClientOption {
  id: string;
  fullName: string;
  conditionSummary: string;
  email: string;
  statusLabel: string;
  client: Client;
}

function getClientDisplayName(client: Client): string {
  const fullName = client.fullName?.trim();
  if (fullName) return fullName;

  const firstName = client.firstName?.trim() ?? '';
  const lastName = client.lastName?.trim() ?? '';
  const fallbackName = `${firstName} ${lastName}`.trim();
  if (fallbackName.length > 0) return fallbackName;

  return client.email?.trim() || 'Unnamed Client';
}

function getClientConditionSummary(client: Client): string {
  const conditionNames = (client.medicalConditionDetails ?? [])
    .map((condition) => formatDisplayLabel(condition.conditionName))
    .filter((conditionName): conditionName is string => Boolean(conditionName));

  if (conditionNames.length > 0) {
    return Array.from(new Set(conditionNames)).slice(0, 2).join(', ');
  }

  const medicalConditions = typeof client.medicalConditions === 'string' ? client.medicalConditions.trim() : '';
  if (medicalConditions.length > 0) {
    return formatDelimitedDisplayLabel(medicalConditions);
  }

  return '';
}

function getClientStatusLabel(client: Client): string {
  const rawStatus = String(client.status ?? '').trim().toLowerCase();
  if (!rawStatus || rawStatus === 'active') return 'Active';
  if (!['inactive', 'suspended', 'archived'].includes(rawStatus)) return 'Active';

  return formatDisplayLabel(rawStatus);
}

function getClientInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) return 'CL';
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
}

export function QuestionnaireFinalDetailShareDialog({
  open,
  shareActionIntent,
  isSubmitting,
  selectedClientId,
  searchText,
  clients,
  isClientsLoading,
  onClose,
  onConfirm,
  onSearchTextChange,
  onClientChange,
}: QuestionnaireFinalDetailShareDialogProps) {
  const [clientListOpen, setClientListOpen] = React.useState(true);

  React.useEffect(() => {
    if (!open || shareActionIntent !== 'fill-manually') return;
    setClientListOpen(true);
  }, [open, shareActionIntent]);

  const fillManualClientOptions = React.useMemo<FillManualClientOption[]>(
    () =>
      clients
        .map((client) => ({
          id: String(client.id),
          fullName: getClientDisplayName(client),
          conditionSummary: getClientConditionSummary(client),
          email: client.email?.trim() || 'No email provided',
          statusLabel: getClientStatusLabel(client),
          client,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [clients]
  );

  const selectedFillManualClient = React.useMemo(
    () => fillManualClientOptions.find((clientOption) => clientOption.id === selectedClientId) ?? null,
    [fillManualClientOptions, selectedClientId]
  );

  const handleClientSelectionChange = React.useCallback(
    (clientId: string) => {
      const normalizedClientId = clientId.trim();
      if (!normalizedClientId) {
        onClientChange('', null);
        return;
      }

      const matchedClient = fillManualClientOptions.find((clientOption) => clientOption.id === normalizedClientId);
      onClientChange(normalizedClientId, matchedClient?.client ?? null);
    },
    [fillManualClientOptions, onClientChange]
  );

  if (shareActionIntent === 'fill-manually') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 32px)', md: 'min(90vw, 720px)', lg: 'min(90vw, 800px)' },
            maxWidth: 800,
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: { xs: '16px', sm: '20px', md: '24px' },
            m: { xs: 1.5, sm: 2 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pt: { xs: 2.5, sm: 3, md: 3.5 }, pb: { xs: 2, md: 2.5 }, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
                  lineHeight: 1.3,
                  color: '#0f172a',
                  wordBreak: 'break-word',
                }}
              >
                Fill Questionnaire for Client
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  lineHeight: 1.4,
                  color: '#667085',
                }}
              >
                Select a client to complete this questionnaire on their behalf.
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                borderRadius: '999px',
                bgcolor: '#26a9da',
                color: '#fff',
                flexShrink: 0,
                '&:hover': { bgcolor: '#1498ca' },
                '&.Mui-disabled': { color: '#fff', bgcolor: '#26a9da', opacity: 0.55 },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <ClickAwayListener onClickAway={() => setClientListOpen(false)}>
            <Box>
              <TextField
                value={searchText}
                onChange={(event) => onSearchTextChange(event.target.value)}
                placeholder="Search Clients..."
                disabled={isSubmitting}
                fullWidth
                sx={{
                  mt: { xs: 1.5, md: 2 },
                  '& .MuiOutlinedInput-root': {
                    height: { xs: 44, sm: 46 },
                    borderRadius: '12px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: 1.4,
                    color: '#344054',
                    '& fieldset': { borderColor: '#d0d5dd' },
                    '&:hover fieldset': { borderColor: '#c3cad4' },
                    '&.Mui-focused fieldset': { borderColor: '#25a8da' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#98a2b3',
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: { xs: 20, sm: 22 } }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography
                sx={{
                  mt: { xs: 2, md: 2.5 },
                  mb: 0.75,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                  lineHeight: 1.4,
                  color: '#1f2937',
                }}
              >
                Client Name
              </Typography>

              <Box
                onClick={() => !isSubmitting && !isClientsLoading && setClientListOpen((o) => !o)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!isSubmitting && !isClientsLoading) setClientListOpen((o) => !o);
                  }
                }}
                sx={{
                  width: '100%',
                  height: { xs: 48, sm: 50 },
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: clientListOpen ? '#25a8da' : '#bcc4ce',
                  bgcolor: '#f9fafb',
                  cursor: isSubmitting || isClientsLoading ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  transition: 'border-color 0.2s',
                  '&:hover': (isSubmitting || isClientsLoading) ? {} : { borderColor: '#25a8da', bgcolor: '#fff' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: 1.4,
                    color: selectedClientId ? '#1f2937' : '#344054',
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedFillManualClient?.fullName ?? 'Select Client'}
                </Typography>
                <ExpandMoreRoundedIcon
                  sx={{
                    fontSize: { xs: 20, sm: 24 },
                    color: '#8b8b8b',
                    flexShrink: 0,
                    ml: 1,
                    transform: clientListOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </Box>

              {clientListOpen && (
                <Box sx={{ mt: 1.5 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '8px',
                      border: '1px solid #e4e7ec',
                      boxShadow: '0 2px 7px rgba(16, 24, 40, 0.08)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        maxHeight: 'min(50vh, 320px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: '1 1 auto',
                        minHeight: 0,
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { backgroundColor: '#eef1f5', borderRadius: '999px' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#26a9da', borderRadius: '999px' },
                      }}
                    >
                      {isClientsLoading ? (
                        <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CircularProgress size={28} thickness={5} sx={{ color: '#26a9da' }} />
                        </Box>
                      ) : fillManualClientOptions.length === 0 ? (
                        <Box sx={{ px: 2, py: 3 }}>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#667085' }}>
                            No clients matched your search.
                          </Typography>
                        </Box>
                      ) : (
                        fillManualClientOptions.map((clientOption, index) => {
                          const isSelected = selectedClientId === clientOption.id;

                          return (
                            <Box
                              key={clientOption.id}
                              onClick={() => {
                                handleClientSelectionChange(clientOption.id);
                                setClientListOpen(false);
                              }}
                              sx={{
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 1.25, sm: 1.5 },
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: { xs: 1.25, sm: 1.5 },
                                cursor: 'pointer',
                                bgcolor: isSelected ? '#e9f5fb' : '#fff',
                                borderBottom:
                                  index < fillManualClientOptions.length - 1
                                    ? '1px solid #edf2f7'
                                    : '1px solid transparent',
                                '&:hover': { bgcolor: isSelected ? '#e2f3fb' : '#f8fbff' },
                              }}
                            >
                              <Box
                                sx={{
                                  width: { xs: 36, sm: 40 },
                                  height: { xs: 36, sm: 40 },
                                  borderRadius: '10px',
                                  bgcolor: '#edf4fd',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <PersonRoundedIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#4b91e3' }} />
                              </Box>

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 500,
                                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                    lineHeight: 1.35,
                                    color: '#344054',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {clientOption.fullName}
                                </Typography>
                                {clientOption.conditionSummary ? (
                                  <Typography
                                    sx={{
                                      fontFamily: 'Inter, sans-serif',
                                      fontWeight: 400,
                                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                      lineHeight: 1.35,
                                      color: '#98a2b3',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {clientOption.conditionSummary}
                                  </Typography>
                                ) : null}
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    lineHeight: 1.35,
                                    color: '#c1c7d0',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {clientOption.email}
                                  {clientOption.statusLabel ? (
                                    <>
                                      <Box component="span" sx={{ px: 0.5 }}>•</Box>
                                      {clientOption.statusLabel}
                                    </>
                                  ) : null}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Divider sx={{ borderColor: '#eceff3', flexShrink: 0 }} />

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 2, md: 2.5 },
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              void onConfirm();
            }}
            disabled={isSubmitting || !selectedClientId || isClientsLoading}
            sx={{
              minWidth: { xs: 120, sm: 140 },
              height: { xs: 44, sm: 46 },
              borderRadius: '10px',
              bgcolor: '#25a8da',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              lineHeight: 1.4,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#b4dff0', color: '#fff' },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                <CircularProgress size={18} thickness={6} sx={{ color: '#fff' }} />
                Selecting...
              </Box>
            ) : (
              'Select Client'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
        {shareActionIntent === 'fill-manually' ? 'Fill Questionnaire Manually' : 'Share Questionnaire Link'}
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#475467' }}>
          Select a client to generate a shareable questionnaire link.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <SearchableClientDropdown
            value={selectedClientId || undefined}
            onChange={(clientId, client) => {
              onClientChange(clientId || '', clientId ? client : null);
            }}
            placeholder="Search and select client..."
            disabled={isSubmitting}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '10px',
            borderColor: '#d0d5dd',
            color: '#344054',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void onConfirm();
          }}
          disabled={isSubmitting || !selectedClientId}
          sx={{
            borderRadius: '10px',
            bgcolor: '#25a8da',
            fontFamily: 'Inter, sans-serif',
            '&:hover': { bgcolor: '#1498ca' },
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} thickness={6} sx={{ color: '#fff' }} />
              Generating...
            </Box>
          ) : shareActionIntent === 'fill-manually' ? (
            'Open Form'
          ) : (
            'Copy Link'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface QuestionnaireManualFillDialogProps {
  open: boolean;
  shareId: string | null;
  questionnaireTitle: string;
  clientName: string;
  /** Client profile when share API does not return assignedClient demographics. */
  assignedClientProfile?: SharedQuestionnaireAssignedClient;
  onClose: () => void;
  onDraftSaved?: (instanceId: string, result: SaveQuestionnaireDraftResponse) => void;
  onSubmitted: (instanceId: string) => void;
}

type AnswerMap = Record<string, JsonAnswerValue>;

function isAnswered(question: JsonQuestion, value: JsonAnswerValue): boolean {
  if (question.type === 'number-unit') {
    return normalizeQuestionAnswerForSubmit(question, value) !== undefined;
  }

  return isAnsweredValue(value);
}

function isDoNotConsentOption(option: JsonQuestionOption): boolean {
  const normalized = `${option.label} ${option.value}`
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.includes('do not consent') || normalized.includes('do not share');
}

function questionHasDoNotConsentOption(question: JsonQuestion): boolean {
  return question.options?.some(isDoNotConsentOption) ?? false;
}

function isDoNotConsentAnswer(question: JsonQuestion, value: JsonAnswerValue): boolean {
  const doNotConsentValues = new Set(
    (question.options ?? []).filter(isDoNotConsentOption).map((option) => option.value)
  );
  if (doNotConsentValues.size === 0) return false;
  if (typeof value === 'string') return doNotConsentValues.has(value);
  if (Array.isArray(value)) return value.some((selectedValue) => doNotConsentValues.has(selectedValue));
  return value === true && (question.options ?? []).length === 1;
}

function hasDoNotConsentSelection(
  section: JsonQuestionnaireSection,
  answers: AnswerMap,
  storageSectionId?: string
): boolean {
  for (const question of getOrderedQuestions(section)) {
    const questionId = getQuestionKey(question);
    const answerKey = storageSectionId ? injectedAnswerKey(storageSectionId, questionId) : questionId;
    if (isDoNotConsentAnswer(question, answers[answerKey])) return true;
  }
  return false;
}

const MANUAL_FILL_AUTO_SAVE_DEBOUNCE_MS = 45_000;

export function QuestionnaireManualFillDialog({
  open,
  shareId,
  questionnaireTitle,
  clientName,
  assignedClientProfile,
  onClose,
  onDraftSaved,
  onSubmitted,
}: QuestionnaireManualFillDialogProps) {
  const nutritionistId = React.useMemo(() => getCurrentUser()?.id ?? undefined, []);
  const [selectedRecallIndex, setSelectedRecallIndex] = React.useState(0);
  const [answersByDay, setAnswersByDay] = React.useState<Record<number, AnswerMap>>({ 0: {} });
  const [errorsByDay, setErrorsByDay] = React.useState<Record<number, Record<string, string>>>({ 0: {} });
  const [triggeredBlocksByDay, setTriggeredBlocksByDay] = React.useState<Record<number, TriggeredBlock[]>>({ 0: [] });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSavingDraft, setIsSavingDraft] = React.useState(false);
  const [draftSavedAt, setDraftSavedAt] = React.useState<string | undefined>();
  const [showConsentDeclinedDialog, setShowConsentDeclinedDialog] = React.useState(false);
  const queryClient = useQueryClient();
  const appliedAssignedPrefillKeysRef = React.useRef<Set<string>>(new Set());
  const appliedAssignedPrefillScopeRef = React.useRef('');
  const appliedSavedResponseFingerprintsRef = React.useRef<Map<string, string>>(new Map());
  const autoSaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFormDirtyRef = React.useRef(false);

  React.useEffect(() => {
    if (!open || !shareId) return;
    setSelectedRecallIndex(0);
    setAnswersByDay({ 0: {} });
    setErrorsByDay({ 0: {} });
    setTriggeredBlocksByDay({ 0: [] });
    setIsSubmitting(false);
    setIsSavingDraft(false);
    setDraftSavedAt(undefined);
    setShowConsentDeclinedDialog(false);
    appliedAssignedPrefillKeysRef.current = new Set();
    appliedAssignedPrefillScopeRef.current = '';
    appliedSavedResponseFingerprintsRef.current = new Map();
    isFormDirtyRef.current = false;
  }, [open, shareId]);

  const {
    data: sharedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['questionnaire-manual-fill-shared', shareId],
    enabled: open && Boolean(shareId),
    queryFn: () => getSharedQuestionnaire(shareId as string),
  });

  const section = sharedData?.section;
  const instanceId = sharedData?.instanceId ?? shareId;
  const isInstanceLocked = isQuestionnaireInstanceLocked(
    sharedData?.alreadySubmitted,
    sharedData?.status
  );
  const assignedClient = React.useMemo(
    () => mergeSharedQuestionnaireAssignedClient(sharedData?.assignedClient, assignedClientProfile),
    [assignedClientProfile, sharedData?.assignedClient]
  );
  const fetchQuestionnaireForTriggers = React.useCallback(
    (id: string) => getQuestionnaireByIdForTriggers(id, nutritionistId),
    [nutritionistId]
  );
  const triggerContext = React.useMemo<TriggerConditionContext | undefined>(
    () => triggerContextFromAssignedClient(assignedClient),
    [assignedClient]
  );
  const assignedPrefill = React.useMemo(
    () =>
      section && assignedClient
        ? buildQuestionnairePrefillFromAssignedClient(section, assignedClient)
        : {},
    [section, assignedClient]
  );
  const assignedPrefillScopeKey = React.useMemo(
    () => buildQuestionnairePrefillScopeKey(section?.id, assignedPrefill),
    [assignedPrefill, section?.id]
  );
  const recallDates = sharedData?.recallDates;
  const dietRecallProgress = sharedData?.dietRecallProgress;
  const numberOfDays = sharedData?.numberOfDays ?? recallDates?.length ?? 0;
  const isMultiDayRecall = numberOfDays > 1 && (recallDates?.length ?? 0) > 0;
  const selectedRecallDate = React.useMemo(
    () => (recallDates && recallDates[selectedRecallIndex] ? recallDates[selectedRecallIndex] : undefined),
    [recallDates, selectedRecallIndex]
  );

  const answers = React.useMemo(
    () => answersByDay[selectedRecallIndex] ?? {},
    [answersByDay, selectedRecallIndex]
  );
  const errors = React.useMemo(
    () => errorsByDay[selectedRecallIndex] ?? {},
    [errorsByDay, selectedRecallIndex]
  );
  const triggeredBlocks = React.useMemo(
    () => triggeredBlocksByDay[selectedRecallIndex] ?? [],
    [triggeredBlocksByDay, selectedRecallIndex]
  );

  const hasConsentDeclined = React.useMemo(() => {
    if (!section) return false;
    if (hasDoNotConsentSelection(section, answers)) return true;
    return triggeredBlocks.some((block) =>
      hasDoNotConsentSelection(block.section, answers, block.meta.questionnaireId)
    );
  }, [answers, section, triggeredBlocks]);

  const shouldDisableQuestionForConsentDecline = React.useCallback(
    (question: JsonQuestion) => hasConsentDeclined && !questionHasDoNotConsentOption(question),
    [hasConsentDeclined]
  );

  const demographicContext = React.useMemo(
    () => resolveQuestionnaireDemographicContext(answers, assignedClient, section ?? null),
    [answers, assignedClient, section]
  );

  React.useEffect(() => {
    if (!open) return;
    setDraftSavedAt(sharedData?.draftSavedAt);
  }, [open, sharedData?.draftSavedAt]);

  React.useEffect(() => {
    if (!open || !section) return;
    if (appliedAssignedPrefillScopeRef.current !== assignedPrefillScopeKey) {
      appliedAssignedPrefillKeysRef.current.clear();
      appliedAssignedPrefillScopeRef.current = assignedPrefillScopeKey;
    }
    const appliedPrefillKeys = appliedAssignedPrefillKeysRef.current;
    const pendingPrefillEntries = Object.entries(assignedPrefill).filter(([key]) => {
      if (appliedPrefillKeys.has(key)) return false;
      appliedPrefillKeys.add(key);
      return true;
    });

    setAnswersByDay((prev) => {
      const dayAnswers = prev[0] ?? {};
      let changed = false;
      const nextDay: AnswerMap = { ...dayAnswers };

      for (const [key, value] of pendingPrefillEntries) {
        const current = nextDay[key];
        if (current === undefined || current === '' || (Array.isArray(current) && current.length === 0)) {
          nextDay[key] = value;
          changed = true;
        }
      }

      const draftPatch = applyPendingSavedResponses(
        section,
        triggeredBlocks,
        sharedData?.savedResponses,
        appliedSavedResponseFingerprintsRef.current
      );
      if (Object.keys(draftPatch).length > 0) {
        Object.assign(nextDay, draftPatch);
        changed = true;
      }

      if (!changed) return prev;
      return { ...prev, 0: nextDay };
    });
  }, [open, section, assignedPrefill, assignedPrefillScopeKey, sharedData?.savedResponses, triggeredBlocks]);

  const completedDayNumbers = new Set<number>(dietRecallProgress?.completedDayNumbers ?? []);
  const totalCount = dietRecallProgress?.total ?? numberOfDays ?? 1;
  const completedCount = completedDayNumbers.size;

  const validate = React.useCallback(() => {
    if (!section) return false;

    const nextErrors: Record<string, string> = {};
    const visibleBase = getVisibleOrderedQuestions(section, answers, demographicContext);
    for (const question of visibleBase) {
      if (!isEffectivelyRequired(question, (id) => answers[id])) continue;

      const key = getQuestionKey(question);
      if (!isAnswered(question, answers[key])) nextErrors[key] = 'This question is required.';
    }

    for (const block of triggeredBlocks) {
      const { section: triggeredSection, meta } = block;
      const storageId = meta.questionnaireId;
      const triggeredAnswers = getTriggeredSectionAnswers(triggeredSection, answers, storageId);
      const visibleTriggered = getVisibleOrderedQuestions(
        triggeredSection,
        triggeredAnswers,
        demographicContext
      );
      for (const question of visibleTriggered) {
        if (!isEffectivelyRequired(question, (id) => triggeredAnswers[id])) continue;

        const key = injectedAnswerKey(storageId, getQuestionKey(question));
        if (!isAnswered(question, answers[key])) nextErrors[key] = 'This question is required.';
      }
    }

    setErrorsByDay((prev) => ({ ...prev, [selectedRecallIndex]: nextErrors }));
    return Object.keys(nextErrors).length === 0;
  }, [section, answers, triggeredBlocks, selectedRecallIndex, demographicContext]);

  const buildSubmitQuestionnaires = React.useCallback(
    (onlyNonEmpty = false): SubmitQuestionnaireBlock[] | null => {
      if (!section) return null;
      const blocks = buildQuestionnaireBlocks(
        section,
        answers,
        triggeredBlocks,
        demographicContext,
        { onlyNonEmpty }
      );
      return blocks.length > 0 ? blocks : null;
    },
    [section, answers, triggeredBlocks, demographicContext]
  );

  const formattedDraftSavedAt = React.useMemo(() => formatDraftSavedAt(draftSavedAt), [draftSavedAt]);

  const onSaveDraft = React.useCallback(
    async (options?: { silent?: boolean }) => {
      if (!instanceId || isInstanceLocked || isMultiDayRecall) return false;

      const questionnaires = buildSubmitQuestionnaires(true);
      if (!questionnaires?.length || countNonEmptyResponsesInBlocks(questionnaires) === 0) {
        if (!options?.silent) {
          showErrorToast(
            new Error('At least one non-empty answer is required'),
            'Add at least one answer before saving progress'
          );
        }
        return false;
      }

      setIsSavingDraft(true);
      try {
        const result = await saveQuestionnaireDraft(instanceId, { questionnaires });
        if (result.savedAt) setDraftSavedAt(result.savedAt);
        isFormDirtyRef.current = false;

        // Keep reopen hydration in sync; otherwise React Query serves the previous draft
        // and apply-once logic can skip updated multi-select answers until a full refresh.
        if (shareId) {
          const nextSavedResponses =
            result.savedResponses?.length
              ? result.savedResponses
              : savedResponsesFromBlocks(questionnaires);
          queryClient.setQueryData<SharedQuestionnaireResponse>(
            ['questionnaire-manual-fill-shared', shareId],
            (current) => {
              if (!current) return current;
              return {
                ...current,
                savedResponses: nextSavedResponses.length ? nextSavedResponses : current.savedResponses,
                draftSavedAt: result.savedAt ?? current.draftSavedAt,
                status: result.status ?? current.status,
              };
            }
          );
          // Seed fingerprints so the hydrate effect does not overwrite newer local edits
          // when this cache write re-triggers after a concurrent change.
          for (const item of nextSavedResponses) {
            appliedSavedResponseFingerprintsRef.current.set(
              item.questionId,
              savedResponseFingerprint(item)
            );
          }
        }

        onDraftSaved?.(result.instanceId || instanceId, result);
        if (!options?.silent) {
          showSuccessToast('Progress saved', 'Draft saved. You can continue later.');
        }
        return true;
      } catch (err) {
        if (!options?.silent) {
          showErrorToast(err as unknown, 'Failed to save progress');
        }
        return false;
      } finally {
        setIsSavingDraft(false);
      }
    },
    [buildSubmitQuestionnaires, instanceId, isInstanceLocked, isMultiDayRecall, onDraftSaved, queryClient, shareId]
  );

  React.useEffect(() => {
    if (!open || isInstanceLocked || isMultiDayRecall || !isFormDirtyRef.current) return;

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      void onSaveDraft({ silent: true });
    }, MANUAL_FILL_AUTO_SAVE_DEBOUNCE_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [open, answers, triggeredBlocks, isInstanceLocked, isMultiDayRecall, onSaveDraft]);

  const handleSubmit = React.useCallback(async () => {
    if (hasConsentDeclined) {
      setShowConsentDeclinedDialog(true);
      return;
    }

    if (!validate() || !shareId) return;

    const questionnaires = buildSubmitQuestionnaires();
    if (!questionnaires?.length) {
      showErrorToast(new Error(INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE), 'Failed to submit questionnaire');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: SubmitQuestionnairePayload = { questionnaires };

      if (isMultiDayRecall && selectedRecallDate) {
        payload.dayNumber = selectedRecallIndex + 1;
        payload.recallDate = selectedRecallDate;
      }

      await submitQuestionnaire(shareId, payload);
      showSuccessToast('Submitted', 'Your responses have been submitted.');
      onClose();
      onSubmitted(shareId);
    } catch (submitError) {
      showErrorToast(submitError as unknown, 'Failed to submit questionnaire');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    buildSubmitQuestionnaires,
    hasConsentDeclined,
    isMultiDayRecall,
    onClose,
    onSubmitted,
    selectedRecallDate,
    selectedRecallIndex,
    shareId,
    validate,
  ]);

  const handleOptionSelect = React.useCallback((_question: JsonQuestion, option: JsonQuestionOption) => {
    if (isDoNotConsentOption(option)) {
      setShowConsentDeclinedDialog(true);
    }
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 32px)', md: 'min(92vw, 960px)' },
            maxWidth: 960,
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: { xs: '20px', sm: '24px', md: '28px' },
            m: { xs: 1.5, sm: 2 },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 28px 80px rgba(15, 23, 42, 0.22)',
          },
        }}
      >
      <Box
        sx={{
          px: { xs: 2.25, sm: 3, md: 5 },
          py: { xs: 2, sm: 2.25, md: 2.75 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          color: '#fff',
          background: 'linear-gradient(95deg, #1d9fd4 0%, #147fb4 100%)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              lineHeight: 1.2,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: alpha('#ffffff', 0.75),
            }}
          >
            Filling For
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 30, sm: 36 },
                height: { xs: 30, sm: 36 },
                borderRadius: '999px',
                bgcolor: alpha('#ffffff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.9375rem' },
                  lineHeight: 1.2,
                  color: '#fff',
                }}
              >
                {getClientInitials(clientName)}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '0.9375rem', sm: '1.125rem', md: '1.25rem' },
                lineHeight: 1.35,
                color: '#fff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {clientName} - {questionnaireTitle}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            width: { xs: 38, sm: 42 },
            height: { xs: 38, sm: 42 },
            borderRadius: '999px',
            color: '#fff',
            bgcolor: alpha('#ffffff', 0.18),
            flexShrink: 0,
            '&:hover': { bgcolor: alpha('#ffffff', 0.28) },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: 'min(85vh, 756px)',
          bgcolor: '#fbfcfe',
        }}
      >
        <Box
          className="manual-fill-form"
          sx={{
            px: { xs: 2.25, sm: 3, md: 5 },
            py: { xs: 2, sm: 2.5, md: 3.25 },
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            '& .space-y-8': { rowGap: '18px' },
            '& .space-y-6': { rowGap: '20px' },
            '& [data-question-card]': {
              marginBottom: 0,
            },
            '& [data-question-card] .MuiOutlinedInput-root': {
              minHeight: { xs: 46, sm: 50 },
              borderRadius: '14px',
              backgroundColor: '#fff',
              boxShadow: 'none',
              border: '0',
            },
            '& [data-question-card] .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d9e0e7',
            },
            '& [data-question-card] .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#25a8da',
            },
            '& [data-question-card] .MuiOutlinedInput-root .MuiOutlinedInput-input': {
              height: { xs: 46, sm: 50 },
              boxSizing: 'border-box',
            },
            '& [data-question-card] input[cmdk-input]': {
              height: 'auto',
              minHeight: 0,
              borderRadius: 0,
              borderColor: 'transparent',
              padding: 0,
            },
            '& [data-question-card] textarea': {
              borderRadius: '14px',
              borderColor: '#d9e0e7',
              backgroundColor: '#fff',
            },
            '& [data-question-card] .MuiOutlinedInput-root.MuiInputBase-multiline': {
              border: '1px solid #d9e0e7',
              backgroundColor: '#fff',
            },
            '& [data-question-card] .MuiOutlinedInput-root.MuiInputBase-multiline:hover': {
              borderColor: '#cbd5e1',
            },
            '& [data-question-card] .MuiOutlinedInput-root.MuiInputBase-multiline.Mui-focused': {
              borderColor: '#25a8da',
            },
            '& [data-question-card] button[role="combobox"]': {
              minHeight: { xs: 46, sm: 50 },
              borderRadius: '14px',
              borderColor: '#d9e0e7',
              backgroundColor: '#fff',
              boxShadow: 'none',
            },
            '& [data-question-card] [cmdk-root] > div': {
              borderRadius: '14px',
              borderColor: '#d9e0e7',
              backgroundColor: '#fff',
            },
            '& [data-question-card] [role="radiogroup"]': {
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            },
            '& [data-question-card] [role="radiogroup"] > div': {
              border: '1px solid #cfd8e3',
              borderRadius: '999px',
              backgroundColor: '#f8fafc',
              height: { xs: 40, sm: 44 },
              paddingLeft: '14px',
              paddingRight: '14px',
              minHeight: { xs: 40, sm: 44 },
              display: 'inline-flex',
              alignItems: 'center',
              width: 'auto',
            },
            '& [data-question-card] [role="radiogroup"] > div [role="radio"]': {
              display: 'none',
            },
            '& [data-question-card] [role="radiogroup"] > div label': {
              marginLeft: '0 !important',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              lineHeight: 1.35,
              fontWeight: 600,
              color: '#4b5565',
            },
            '& [data-question-card] [role="radiogroup"] > div:has([data-state="checked"])': {
              borderColor: '#19a7d9',
              backgroundColor: '#e9f6fc',
            },
            '& [data-question-card] [role="radiogroup"] > div:has([data-state="checked"]) label': {
              color: '#0b88bc',
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={30} thickness={5} sx={{ color: '#25a8da' }} />
            </Box>
          ) : isError ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#1f2937' }}>
                Could not load questionnaire
              </Typography>
              <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#667085' }}>
                {(error as Error | undefined)?.message ?? 'An unexpected error occurred while loading the form.'}
              </Typography>
            </Box>
          ) : !section ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#1f2937' }}>
                Questionnaire not found
              </Typography>
              <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#667085' }}>
                This shared questionnaire instance could not be found.
              </Typography>
            </Box>
          ) : isInstanceLocked && !isMultiDayRecall ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#1f2937' }}>
                Questionnaire already submitted
              </Typography>
              <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#667085' }}>
                This questionnaire has already been submitted and can no longer be edited.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {isMultiDayRecall && recallDates && recallDates.length > 0 ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1f2937',
                    }}
                  >
                    Select recall day
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.4,
                      mb: 1.2,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      color: '#667085',
                    }}
                  >
                    Completed {completedCount} of {totalCount}.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {recallDates.map((dateStr, index) => {
                      const isSelected = selectedRecallIndex === index;
                      const isCompleted = completedDayNumbers.has(index + 1);

                      return (
                        <Button
                          key={dateStr}
                          type="button"
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => setSelectedRecallIndex(index)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '999px',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            minHeight: 30,
                            px: 1.4,
                            ...(isSelected
                              ? {
                                  bgcolor: '#25a8da',
                                  color: '#fff',
                                  '&:hover': { bgcolor: '#1498ca' },
                                }
                              : {
                                  borderColor: isCompleted ? '#10b981' : '#d0d5dd',
                                  color: isCompleted ? '#047857' : '#475467',
                                  bgcolor: isCompleted ? '#ecfdf3' : '#fff',
                                  '&:hover': {
                                    borderColor: isCompleted ? '#10b981' : '#98a2b3',
                                    bgcolor: isCompleted ? '#e6f9ef' : '#f8fafc',
                                  },
                                }),
                          }}
                        >
                          Day {index + 1}: {dateStr}
                          {isCompleted ? ' ✓' : ''}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              ) : null}

              <DynamicQuestionnaireFlow
                section={section}
                answers={answers}
                triggerContext={triggerContext}
                assignedClient={assignedClient}
                hideTitle
                hideDescription
                hideQuestionNotes
                questionVariant="popup"
                onAnswerChange={(questionKey, value) => {
                  isFormDirtyRef.current = true;
                  setAnswersByDay((prev) => ({
                    ...prev,
                    [selectedRecallIndex]: { ...(prev[selectedRecallIndex] ?? {}), [questionKey]: value },
                  }));

                  setErrorsByDay((prev) => {
                    if (!prev[selectedRecallIndex]?.[questionKey]) return prev;
                    const nextByDay = { ...prev, [selectedRecallIndex]: { ...(prev[selectedRecallIndex] ?? {}) } };
                    delete nextByDay[selectedRecallIndex][questionKey];
                    return nextByDay;
                  });
                }}
                errors={errors}
                fetchQuestionnaire={fetchQuestionnaireForTriggers}
                onTriggeredChange={(blocks) =>
                  setTriggeredBlocksByDay((prev) => ({ ...prev, [selectedRecallIndex]: blocks }))
                }
                onOptionSelect={handleOptionSelect}
                isQuestionDisabled={shouldDisableQuestionForConsentDecline}
              />

            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: '#eceff3' }} />

        <Box
          sx={{
            px: { xs: 2.25, sm: 3, md: 5 },
            py: { xs: 1.25, sm: 1.5, md: 1.75 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            bgcolor: '#fff',
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '0.8125rem', sm: '0.875rem' }, color: '#667085' }}>
              Filling for <Box component="span" sx={{ fontWeight: 700, color: '#1f2937' }}>{clientName}</Box>
            </Typography>
            {!isInstanceLocked && !isMultiDayRecall && formattedDraftSavedAt ? (
              <Typography sx={{ mt: 0.35, fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#98a2b3' }}>
                Last saved {formattedDraftSavedAt}
                {isSavingDraft ? ' · Saving…' : ''}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting || isSavingDraft}
              sx={{
                minWidth: { xs: 72, sm: 80 },
                height: { xs: 34, sm: 36 },
                px: { xs: 1.5, sm: 2 },
                borderRadius: '10px',
                borderColor: '#d0d5dd',
                color: '#344054',
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                lineHeight: 1.4,
                textTransform: 'none',
                '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
              }}
            >
              Cancel
            </Button>
            {!isInstanceLocked && !isMultiDayRecall ? (
              <Button
                variant="outlined"
                onClick={() => {
                  void onSaveDraft();
                }}
                disabled={isSavingDraft || isSubmitting || !section || isLoading || isError || hasConsentDeclined}
                sx={{
                  minWidth: { xs: 96, sm: 108 },
                  height: { xs: 34, sm: 36 },
                  px: { xs: 1.5, sm: 2 },
                  borderRadius: '10px',
                  borderColor: '#d0d5dd',
                  color: '#344054',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  lineHeight: 1.4,
                  textTransform: 'none',
                  '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
                }}
              >
                {isSavingDraft ? 'Saving…' : 'Save progress'}
              </Button>
            ) : null}
            {!isInstanceLocked ? (
              <Button
                variant="contained"
                onClick={() => {
                  void handleSubmit();
                }}
                disabled={isSubmitting || isSavingDraft || !section || isLoading || isError || hasConsentDeclined}
                sx={{
                  minWidth: { xs: 72, sm: 80 },
                  height: { xs: 34, sm: 36 },
                  px: { xs: 1.75, sm: 2.25 },
                  borderRadius: '10px',
                  background: 'linear-gradient(95deg, #25a8da 0%, #148bc0 100%)',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  fontWeight: 700,
                  lineHeight: 1.4,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { background: 'linear-gradient(95deg, #1498ca 0%, #0f7dac 100%)' },
                  '&.Mui-disabled': { background: '#b4dff0', color: '#fff' },
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CircularProgress size={12} thickness={6} sx={{ color: '#fff' }} />
                    Submitting...
                  </Box>
                ) : (
                  'Submit'
                )}
              </Button>
            ) : null}
          </Box>

          {!isInstanceLocked && Object.keys(errors).length > 0 ? (
            <Typography sx={{ width: '100%', fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#dc2626' }}>
              Please answer all required questions before submitting.
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      </Dialog>

      <Dialog
        open={showConsentDeclinedDialog}
        onClose={() => setShowConsentDeclinedDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            p: 0.5,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '18px', color: '#1f2937' }}>
          Information sharing declined
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', lineHeight: 1.65, color: '#667085' }}>
            Please inform your practitioner that you have chosen not to share this information. This may
            result in them having limited information to support your personalized care.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setShowConsentDeclinedDialog(false)}
            sx={{
              borderRadius: '10px',
              bgcolor: '#20AEDC',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              '&:hover': { bgcolor: '#1a99c0' },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
